import { Component, inject, input, viewChild, ChangeDetectionStrategy } from '@angular/core';

import { merge, Observable, Subject } from 'rxjs';

import { toObservable } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { switchMap } from 'rxjs/operators';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { ConfigurationPayloadComponent } from '../../../../sub-features/apple-profiles/profile-components/configuration-payload/configuration-payload.component';
import { ConfigurationProfileComponent } from '../../../../sub-features/apple-profiles/profile-components/configuration-profile/configuration-profile.component';
import { WifiProfileComponent } from '../../../../sub-features/apple-profiles/profile-components/wifi-profile/wifi-profile.component';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { ConfigurationProfile } from '../../../../viewmodels/apple/configuration-profile.model';
import { WifiProfile } from '../../../../viewmodels/apple/profiles/wifi-profile/wifi-profile.model';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

@Component({
  selector: 'ml-mdm-apple-create-wifi-profile',
  templateUrl: './apple-create-wifi-profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommandSpinnerComponent,
    WifiProfileComponent,
    ConfigurationPayloadComponent,
    ConfigurationProfileComponent,
    FaIconComponent,
  ],
})
export class AppleCreateWifiProfileComponent {
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly mdmService = inject(AppleMdmService);

  private readonly wifiProfileComponent = viewChild.required(WifiProfileComponent);

  private readonly configurationProfileComponent = viewChild.required(ConfigurationProfileComponent);

  public wifiProfile: WifiProfile;

  public readonly udid = input.required<string>();
  private readonly _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  public readonly commandProgress$: Observable<CommandProgress> = toObservable(this.udid).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'InstallProfile'))
    )
  );
  public configurationProfile = new ConfigurationProfile();
  public showMore = false;

  constructor() {
    this.configurationProfile.payloadIdentifier = 'com.methylium.wifi.guest';
    this.configurationProfile.payloadOrganization = 'Methylium B.V.';
    this.configurationProfile.payloadDescription = 'This profile has pre-configured settings for Wi-Fi.';
    this.configurationProfile.payloadDisplayName = 'Wi-Fi';

    this.wifiProfile = new WifiProfile();
    this.wifiProfile.payloadDescription = 'Wi-Fi profile configures the device to use the specified Wi-Fi network.';
    this.wifiProfile.payloadDisplayName = 'Wi-Fi Profile';
    this.wifiProfile.payloadIdentifier = 'com.methylium.wifi.test';
    this.wifiProfile.payloadOrganization = 'Methylium B.V.';
    this.wifiProfile.payloadVersion = 1;
  }

  public installProfile(_udid: string) {
    // Validate and sync wifi profile form
    const wifiProfileComponent = this.wifiProfileComponent();
    const formComponent = wifiProfileComponent?.formComponent();
    if (formComponent?.form) {
      if (!formComponent.form.valid) {
        return;
      }
      // Sync form values to wifiProfile
      if (formComponent.model) {
        const model = formComponent.model;
        // Filter out empty strings for optional fields to avoid validation errors
        const cleanedModel: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(model)) {
          // Keep all values except empty strings for string fields (unless it's a required field)
          if (value !== '' || key === 'SSID_STR') {
            cleanedModel[key] = value;
          }
        }
        // Update wifiProfile properties directly to preserve the instance
        Object.assign(this.wifiProfile, cleanedModel);
        // Also update the component's wifiProfile for two-way binding
        wifiProfileComponent.onFormSubmit(cleanedModel);
      }
    }

    // Try to stop editing on configuration profile component
    if (!this.configurationProfileComponent().doneEditing()) {
      return;
    }

    const command: CommandProgress = {
      udid: _udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'InstallProfile',
    };
    this._commandProgress.next(command);

    this.configurationProfile.payloadContent = [this.wifiProfile];
    this.mdmService.installProfile(_udid, this.configurationProfile).subscribe();
  }
}
